export const MAC_RELEASE_SCHEMA_VERSION = 1 as const;

export interface MacReleaseManifest {
  readonly architecture: string;
  readonly downloadUrl: string;
  readonly filename: string;
  readonly minimumSystemVersion: string;
  readonly notarized: boolean;
  readonly publishedAt: string;
  readonly releaseNotes: readonly string[];
  readonly schemaVersion: typeof MAC_RELEASE_SCHEMA_VERSION;
  readonly sha256: string;
  readonly version: string;
}

/**
 * Keep this URL stable for already-distributed clients. The Worker may redirect
 * it to a different immutable release host, but changing the manifest back to a
 * new host can make older app allowlists reject the entire update response.
 */
export const MAC_RELEASE_DOWNLOAD_BASE_URL =
  "https://banryeojusik-dev.kimhighstar.workers.dev/api/releases/macos/download";

/**
 * Preserve every published mapping: cached old manifests must continue to
 * resolve to their own immutable artifact after a newer release is activated.
 */
export const MAC_RELEASE_ARTIFACTS: Readonly<Record<string, string>> = {
  "0.2.4":
    "https://github.com/kys42/banryeojusik-releases/releases/download/v0.2.4-preview.1/banryeojusik-0.2.4-macos-universal-preview.dmg",
  "0.3.0":
    "https://github.com/kys42/banryeojusik-releases/releases/download/v0.3.0-preview.1/banryeojusik-0.3.0-macos-universal-preview.dmg",
  "0.3.1":
    "https://github.com/kys42/banryeojusik-releases/releases/download/v0.3.1-preview.1/banryeojusik-0.3.1-macos-universal-preview.dmg",
  "0.3.2":
    "https://github.com/kys42/banryeojusik-releases/releases/download/v0.3.2/banryeojusik-0.3.2-macos-universal-notarized.dmg",
  "0.3.3":
    "https://github.com/kys42/banryeojusik-releases/releases/download/v0.3.3/banryeojusik-0.3.3-macos-universal-notarized.dmg",
  "0.4.0":
    "https://github.com/kys42/banryeojusik-releases/releases/download/v0.4.0/banryeojusik-0.4.0-macos-universal-notarized.dmg",
  "0.4.1":
    "https://github.com/kys42/banryeojusik-releases/releases/download/v0.4.1/banryeojusik-0.4.1-macos-universal-notarized.dmg",
  "0.4.2":
    "https://github.com/kys42/banryeojusik-releases/releases/download/v0.4.2/banryeojusik-0.4.2-macos-universal-notarized.dmg",
  "0.5.0":
    "https://github.com/kys42/banryeojusik-releases/releases/download/v0.5.0/banryeojusik-0.5.0-macos-universal-notarized.dmg",
};

export const MAC_RELEASE_DOWNLOAD_URL = `${MAC_RELEASE_DOWNLOAD_BASE_URL}?version=0.5.0`;

export const CURRENT_MAC_RELEASE: MacReleaseManifest = {
  schemaVersion: MAC_RELEASE_SCHEMA_VERSION,
  version: "0.5.0",
  filename: "banryeojusik-0.5.0-macos-universal-notarized.dmg",
  architecture: "Apple Silicon + Intel",
  minimumSystemVersion: "macOS 12 Monterey",
  publishedAt: "2026-08-06",
  notarized: true,
  downloadUrl: MAC_RELEASE_DOWNLOAD_URL,
  sha256:
    "e0319264e28c7eb7b9fa7680280483a8c3d1bee9fa0fa81c48a36692a82c557f",
  releaseNotes: [
    "Mac 로그인 시 메뉴바에서 자동으로 시작하고, 한 번의 빠른 메뉴로 농장을 관리해요.",
    "버튼을 누르기 전 5초의 농장을 GIF나 영상으로 바로 공유하고 반복 미리보기로 확인해요.",
    "한국어와 English를 선택하고, 더 자연스럽게 농장을 돌아다니는 슬라임을 만나요.",
  ],
};

const STABLE_VERSION_PATTERN = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;
const SHA256_PATTERN = /^[a-f0-9]{64}$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function compareStableVersions(left: string, right: string): number {
  const leftParts = parseStableVersion(left);
  const rightParts = parseStableVersion(right);
  if (!leftParts || !rightParts) {
    throw new Error("invalid_stable_version");
  }

  for (let index = 0; index < leftParts.length; index += 1) {
    const difference = leftParts[index] - rightParts[index];
    if (difference !== 0) {
      return Math.sign(difference);
    }
  }
  return 0;
}

export function parseMacReleaseManifest(
  candidate: unknown,
): MacReleaseManifest | null {
  if (!isRecord(candidate)) {
    return null;
  }

  const {
    architecture,
    downloadUrl,
    filename,
    minimumSystemVersion,
    notarized,
    publishedAt,
    releaseNotes,
    schemaVersion,
    sha256,
    version,
  } = candidate;
  if (
    schemaVersion !== MAC_RELEASE_SCHEMA_VERSION ||
    typeof version !== "string" ||
    !parseStableVersion(version) ||
    typeof filename !== "string" ||
    !filename.endsWith(".dmg") ||
    typeof architecture !== "string" ||
    architecture.trim().length === 0 ||
    typeof minimumSystemVersion !== "string" ||
    minimumSystemVersion.trim().length === 0 ||
    typeof publishedAt !== "string" ||
    !ISO_DATE_PATTERN.test(publishedAt) ||
    typeof notarized !== "boolean" ||
    typeof downloadUrl !== "string" ||
    !isAllowedHttpsDownload(downloadUrl) ||
    typeof sha256 !== "string" ||
    !SHA256_PATTERN.test(sha256) ||
    !Array.isArray(releaseNotes) ||
    releaseNotes.length > 3 ||
    !releaseNotes.every(
      (note) =>
        typeof note === "string" &&
        note.trim().length > 0 &&
        note.length <= 120,
    )
  ) {
    return null;
  }

  return {
    architecture,
    downloadUrl,
    filename,
    minimumSystemVersion,
    notarized,
    publishedAt,
    releaseNotes: [...releaseNotes],
    schemaVersion,
    sha256,
    version,
  };
}

function parseStableVersion(version: string): readonly number[] | null {
  const match = STABLE_VERSION_PATTERN.exec(version);
  return match ? match.slice(1).map(Number) : null;
}

function isAllowedHttpsDownload(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      (url.hostname === "drive.google.com" ||
        url.hostname === "banryeojusik-dev.kimhighstar.workers.dev" ||
        (url.hostname === "github.com" &&
          url.pathname.startsWith(
            "/kys42/banryeojusik-releases/releases/download/",
          )))
    );
  } catch {
    return false;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
